import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Shield, Download, ArrowLeft, Search, Filter, RefreshCw,
  FileText, User, Building2, Clock, AlertTriangle, CheckCircle,
  Loader2, ChevronLeft, ChevronRight, FileSpreadsheet, FileDown
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';
import API_URL from '../lib/apiUrl';
import jsPDF from 'jspdf';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../contexts/AuthContext';


const AuditLogs = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [filters, setFilters] = useState({
    action: '',
    resource_type: '',
    user_email: ''
  });

  const canViewAuditLogs = user && ['super_admin', 'admin', 'org_admin', 'security_officer'].includes(user?.role);

  const fetchLogs = useCallback(async () => {
    if (!canViewAuditLogs) return;
    
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (filters.action && filters.action !== 'all') params.append('action', filters.action);
      if (filters.resource_type && filters.resource_type !== 'all') params.append('resource_type', filters.resource_type);
      if (filters.user_email) params.append('user_email', filters.user_email);
      
      const response = await axios.get(`${API_URL}/api/audit-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setLogs(response.data.logs);
      setTotalPages(response.data.total_pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [page, filters, token, canViewAuditLogs]);

  const fetchStats = useCallback(async () => {
    if (!canViewAuditLogs) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/audit-logs/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, [token, canViewAuditLogs]);

  useEffect(() => {
    fetchLogs();
    fetchStats();
  }, [fetchLogs, fetchStats]);

  const handleExport = async (format) => {
    setExporting(true);
    toast.info(`Exporting to ${format.toUpperCase()}...`);
    
    try {
      if (format === 'pdf') {
        // Get all logs for PDF
        const response = await axios.get(`${API_URL}/api/audit-logs/export/pdf`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const { logs: allLogs, exported_by, exported_at, total_records } = response.data;
        
        // Generate PDF
        const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape
        const pageWidth = pdf.internal.pageSize.getWidth();
        const margin = 15;
        let yPos = margin;

        // Title
        pdf.setFillColor(30, 41, 59);
        pdf.rect(0, 0, pageWidth, 40, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(20);
        pdf.setFont('helvetica', 'bold');
        pdf.text('Audit Log Report', margin, 20);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Exported by: ${exported_by} | Date: ${new Date(exported_at).toLocaleString()} | Total Records: ${total_records}`, margin, 32);
        
        yPos = 50;
        
        // Table header
        pdf.setFillColor(241, 245, 249);
        pdf.rect(margin, yPos, pageWidth - 2 * margin, 10, 'F');
        pdf.setTextColor(30, 41, 59);
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        
        const colWidths = [45, 35, 30, 30, 80, 50];
        const headers = ['Timestamp', 'User', 'Role', 'Action', 'Details', 'Resource'];
        let xPos = margin;
        headers.forEach((header, i) => {
          pdf.text(header, xPos + 2, yPos + 7);
          xPos += colWidths[i];
        });
        
        yPos += 12;
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        
        // Table rows
        allLogs.forEach((log, index) => {
          if (yPos > pdf.internal.pageSize.getHeight() - 20) {
            pdf.addPage();
            yPos = margin;
          }
          
          // Alternate row colors
          if (index % 2 === 0) {
            pdf.setFillColor(248, 250, 252);
            pdf.rect(margin, yPos - 3, pageWidth - 2 * margin, 8, 'F');
          }
          
          pdf.setTextColor(71, 85, 105);
          xPos = margin;
          
          const rowData = [
            new Date(log.timestamp).toLocaleString(),
            log.user_email?.substring(0, 20) || 'N/A',
            log.user_role || 'N/A',
            log.action || 'N/A',
            (log.details || 'N/A').substring(0, 50),
            `${log.resource_type || ''} ${log.resource_id?.substring(0, 8) || ''}`
          ];
          
          rowData.forEach((data, i) => {
            pdf.text(data, xPos + 2, yPos + 2);
            xPos += colWidths[i];
          });
          
          yPos += 8;
        });
        
        pdf.save(`audit_logs_${new Date().toISOString().split('T')[0]}.pdf`);
        toast.success('PDF exported successfully');
      } else {
        // CSV or Excel - download from server
        const response = await axios.get(`${API_URL}/api/audit-logs/export/${format}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        });
        
        const blob = new Blob([response.data], { 
          type: format === 'csv' ? 'text/csv' : 'application/vnd.ms-excel' 
        });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.${format === 'excel' ? 'xlsx' : format}`;
        a.click();
        window.URL.revokeObjectURL(url);
        
        toast.success(`${format.toUpperCase()} exported successfully`);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export audit logs');
    } finally {
      setExporting(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case 'login': return <User className="h-4 w-4 text-green-600" />;
      case 'create': return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case 'update': return <RefreshCw className="h-4 w-4 text-yellow-600" />;
      case 'delete': return <AlertTriangle className="h-4 w-4 text-red-600" />;
      case 'export': return <Download className="h-4 w-4 text-purple-600" />;
      case 'view': return <FileText className="h-4 w-4 text-slate-600" />;
      case 'invite': return <User className="h-4 w-4 text-indigo-600" />;
      default: return <FileText className="h-4 w-4 text-slate-400" />;
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'login': return 'bg-green-100 text-green-700';
      case 'create': return 'bg-blue-100 text-blue-700';
      case 'update': return 'bg-yellow-100 text-yellow-700';
      case 'delete': return 'bg-red-100 text-red-700';
      case 'export': return 'bg-purple-100 text-purple-700';
      case 'view': return 'bg-slate-100 text-slate-700';
      case 'invite': return 'bg-indigo-100 text-indigo-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-slate-900 mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!canViewAuditLogs) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Shield className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h1>
          <p className="text-slate-600 mb-6">You don&apos;t have permission to view audit logs.</p>
          <Button onClick={() => navigate('/dashboard')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 py-8">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Shield className="h-8 w-8 text-slate-900" />
                <h1 className="font-heading font-bold text-3xl text-black">Audit Logs</h1>
              </div>
              <p className="text-slate-600">Track all user activities and system events</p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => navigate('/admin')}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Admin
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <FileText className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Logs</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_logs}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Clock className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Last 24 Hours</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.recent_activity_24h}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-purple-100 rounded-lg">
                    <Download className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Total Exports</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.total_exports}</p>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-yellow-100 rounded-lg">
                    <User className="h-6 w-6 text-yellow-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Logins</p>
                    <p className="text-2xl font-bold text-slate-900">{stats.action_counts?.login || 0}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filters and Export */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-slate-200 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search by email..."
                    value={filters.user_email}
                    onChange={(e) => setFilters({ ...filters, user_email: e.target.value })}
                    className="pl-10 text-black"
                  />
                </div>
              </div>
              <Select
                value={filters.action}
                onValueChange={(value) => setFilters({ ...filters, action: value })}
              >
                <SelectTrigger className="w-[150px]">
                  <Filter className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  <SelectItem value="login">Login</SelectItem>
                  <SelectItem value="create">Create</SelectItem>
                  <SelectItem value="update">Update</SelectItem>
                  <SelectItem value="delete">Delete</SelectItem>
                  <SelectItem value="export">Export</SelectItem>
                  <SelectItem value="view">View</SelectItem>
                  <SelectItem value="invite">Invite</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.resource_type}
                onValueChange={(value) => setFilters({ ...filters, resource_type: value })}
              >
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Resource" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Resources</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                  <SelectItem value="organization">Organization</SelectItem>
                  <SelectItem value="assessment">Assessment</SelectItem>
                  <SelectItem value="audit_log">Audit Log</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchLogs}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  onClick={() => handleExport('csv')}
                  disabled={exporting}
                >
                  <FileDown className="h-4 w-4 mr-2" />
                  CSV
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => handleExport('excel')}
                  disabled={exporting}
                >
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Excel
                </Button>
                <Button 
                  onClick={() => handleExport('pdf')}
                  disabled={exporting}
                >
                  {exporting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  PDF
                </Button>
              </div>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-4" />
                <p className="text-slate-500">Loading audit logs...</p>
              </div>
            ) : logs.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No audit logs found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm">Timestamp</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm">User</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm">Role</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm">Action</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm">Resource</th>
                        <th className="text-left py-3 px-4 font-semibold text-slate-700 text-sm">Details</th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log, index) => (
                        <tr 
                          key={log.log_id} 
                          className={`border-b border-slate-100 hover:bg-slate-50 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-25'}`}
                        >
                          <td className="py-3 px-4 text-sm text-slate-600">
                            <div className="flex items-center gap-2">
                              <Clock className="h-3 w-3 text-slate-400" />
                              {new Date(log.timestamp).toLocaleString()}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center">
                                <User className="h-4 w-4 text-slate-600" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-slate-900">{log.user_name}</p>
                                <p className="text-xs text-slate-500">{log.user_email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 font-medium">
                              {log.user_role?.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {getActionIcon(log.action)}
                              <span className={`text-xs px-2 py-1 rounded font-medium ${getActionBadgeColor(log.action)}`}>
                                {log.action}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {log.resource_type === 'organization' && <Building2 className="h-4 w-4 text-slate-400" />}
                              {log.resource_type === 'user' && <User className="h-4 w-4 text-slate-400" />}
                              {log.resource_type === 'assessment' && <FileText className="h-4 w-4 text-slate-400" />}
                              <span className="text-sm text-slate-600">{log.resource_type}</span>
                              {log.resource_id && (
                                <span className="text-xs text-slate-400">
                                  #{log.resource_id.substring(0, 8)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <p className="text-sm text-slate-600 max-w-xs truncate" title={log.details}>
                              {log.details || '-'}
                            </p>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                  <p className="text-sm text-slate-600">
                    Showing {((page - 1) * 20) + 1} to {Math.min(page * 20, total)} of {total} logs
                  </p>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-slate-600">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default AuditLogs;
